import { BehaviorSubject, finalize, forkJoin, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env';
import { HttpClient } from '@angular/common/http';
import { PurchaseReceiptDocument, ReceiptListRes, ReceiptProduct, ReceiptProductParent, ReceiptProfile, ReceiptProfileRaw } from '../types/receipt.type';
import { buildHttpParams } from '@app/shared/utils';
import { MessageService } from '@app/shared/services/message.service';

@Injectable()
export class ReceiptApiService {
	readonly #httpClient = inject(HttpClient);
	readonly #notify = inject(MessageService);

	readonly #loadingSub$ = new BehaviorSubject(false);
	readonly #loadingProfileSub$ = new BehaviorSubject(false);

	loadingList$ = this.#loadingSub$.asObservable().pipe(
		shareReplay(1)
	)
	loadingProfile$ = this.#loadingProfileSub$.asObservable().pipe(
		shareReplay(1)
	)

	fetchOperations(shopId: number): Observable<ReceiptListRes> {
		const params = buildHttpParams({ status: 'DRAFT', shops: shopId });

		this.#loadingSub$.next(true);
		return this.#httpClient.get<ReceiptListRes>(`${environment.apiUrl}/purchase/receipts`, { params }).pipe(
			finalize(() => this.#loadingSub$.next(false))
		);
	}

	validateOperationWithFiles({ id_operation, comment, bl_number, files }: { id_operation: number, comment: string | null, bl_number: string | null, files: File[] | null }): Observable<ReceiptProfile> {
		this.#loadingProfileSub$.next(true);
		// @ts-ignore
		return (
				files?.length!!
					? forkJoin(
						files.map(file => this.#uploadBLDocument({ id_operation, file }))
					)
					: of(null)
			)
			.pipe(
				// @ts-ignore
				switchMap(() => this.#validateOperation({ id_operation, comment, bl_number })),
				finalize(() => this.#loadingProfileSub$.next(false))
			)
	}

	deleteOperation({ id_operation }: { id_operation: number }): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.delete<number>(`${environment.apiUrl}/shops/operation/RECEIPT/${id_operation}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	fetchOperationProfile({ id_operation }: { id_operation: number }): Observable<ReceiptProfile> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.get<ReceiptProfileRaw>(`${environment.apiUrl}/purchase/receipt/${id_operation}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false)),
			map((receipt) => this.#prepareReceipt(receipt))
		);
	}

	addOperationProduct(
		{ id_operation, input }:
		{
			id_operation: number,
			input: { id_product: number, quantity: number, price: number | null, packaging: number | null }
		}
	): Observable<{ success: boolean, warnings: string[] }> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.post<{ success: boolean, warnings: string[] }>(`${environment.apiUrl}/shops/operation/RECEIPT/${id_operation}/product`, input).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	updateOperationProduct(
		{ id_operation, id_reference, input }:
		{
			id_operation: number,
			id_reference: number,
			input: { id_product: number, quantity: number, price?: number | undefined, packaging?: number | undefined }
		}
	): Observable<{ success: boolean, warnings: string[] }> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.patch<{ success: boolean, warnings: string[] }>(`${environment.apiUrl}/shops/operation/RECEIPT/${id_operation}/product/${id_reference}`, input).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	deleteOperationProduct(
		{ id_operation, id_reference }:
		{ id_operation: number, id_reference: number }
	): Observable<number> {
		this.#loadingProfileSub$.next(true);
		return this.#httpClient.delete<number>(`${environment.apiUrl}/shops/operation/RECEIPT/${id_operation}/product/${id_reference}`).pipe(
			finalize(() => this.#loadingProfileSub$.next(false))
		);
	}

	#uploadBLDocument({ id_operation, file }: { id_operation: number, file: File }): Observable<{ ok: boolean, document: PurchaseReceiptDocument }> {
		const formData = new FormData();
		formData.set('type', 'DELIVERY_NOTE_PICTURE');
		formData.append('picture', file, file.name);
		return this.#httpClient.post<{ ok: boolean, document: PurchaseReceiptDocument }>(`${environment.apiUrl}/purchase/receipt/${id_operation}/document`, formData);
	}

	#validateOperation({ id_operation, comment, bl_number }: { id_operation: number, comment: string | null, bl_number: string | null }): Observable<ReceiptProfile> {
		return this.#httpClient.post<ReceiptProfileRaw>(`${environment.apiUrl}/purchase/receipt/${id_operation}/validate`, { comment, bl_number }).pipe(
			tap(() => this.#notify.show('La réception a bien été validée et les stocks ont été mis à jour', 'alert-success')),
			map((receipt) => this.#prepareReceipt(receipt))
		);
	}

	#prepareReceipt(receipt: ReceiptProfileRaw): ReceiptProfile {
		return {
			...receipt,
			products: receipt.products.map(
				p => (p as ReceiptProductParent).variants ? (p as ReceiptProductParent).variants : p as ReceiptProduct
			).flat()
		}
	}
}
